import { Link } from 'react-router-dom'
import { eOrigemPadrao } from '../../../api/filtrosDeGmud'
import { EtiquetaDeOrigem, EtiquetaDeStatus } from '../../../components/Etiqueta'
import { IconeCadeado, IconeHistorico } from '../../../components/Icones'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import type { OrigemGmud, ResumoGmud } from '../../../types/gmud'

/**
 * As marcas que a tabela e os cartões do celular precisam mostrar do mesmo jeito.
 *
 * Ficam num arquivo só porque as duas apresentações da MESMA lista não podem divergir: se o
 * cartão do celular esquecer a marca de histórico, o fiscal que abre o sistema no telefone lê
 * 1.027 registros importados como se fossem do processo novo — e é justamente no telefone que
 * ele confere as coisas correndo.
 */

/**
 * O que a coluna "Estado" mostra em cada linha.
 *
 * Em registro do processo novo, o estado. Em registro importado, a PROCEDÊNCIA — porque ele não
 * está em etapa nenhuma: não foi aprovado por comitê, não aguarda pauta, não foi concluído no
 * sentido do ANEXO XXI. Escrever "Aprovada" numa linha dessas seria o sistema afirmar uma
 * decisão de comitê que nunca houve.
 *
 * A etiqueta de estado ainda aparece quando o registro importado NÃO está em `MIGRADO_V1`. Hoje
 * todos estão, mas isso é coincidência da importação e não definição: origem é fixada e não
 * muda, estado é etapa e muda. Se um dia divergirem, a linha continua contando as duas coisas.
 *
 * @param props a linha da listagem
 * @returns a etiqueta de estado, ou a marca de histórico com o rótulo original
 */
export function EstadoNaLista({ gmud }: { gmud: ResumoGmud }) {
  if (gmud.origem !== 'MIGRADO_V1') {
    return <EtiquetaDeStatus status={gmud.status} rotulo={gmud.statusRotulo} />
  }

  return (
    <div className="space-y-1">
      <EtiquetaDeOrigem origem={gmud.origem} />
      {gmud.status !== 'MIGRADO_V1' && <EtiquetaDeStatus status={gmud.status} rotulo={gmud.statusRotulo} />}
      {gmud.statusOriginalV1 && (
        // O rótulo exato do sistema anterior, e não uma tradução: é a palavra que está nos
        // documentos já entregues à fiscalização, e é por ela que alguém procura o registro.
        <p className="text-xs text-slate-600">
          No sistema anterior: <span className="font-medium text-slate-800">{gmud.statusOriginalV1}</span>
        </p>
      )}
    </div>
  )
}

/**
 * Cadeado de "ainda não liberada para produção".
 *
 * Só o desenho, sem a palavra ao lado, e a razão é a frequência: `bloqueado_para_deploy` nasce
 * verdadeiro e só a aprovação do comitê o desliga (V4 do schema), então ele é verdadeiro em
 * quase toda linha da lista — rascunho, aguardando pauta, recusada e o histórico inteiro. Texto
 * repetido em oito de cada dez linhas deixa de ser lido e passa a ser ruído em volta do que
 * muda de uma linha para a outra. O `<title>` do SVG carrega a explicação do glossário: é o
 * nome que o leitor de tela anuncia e a dica que o navegador mostra ao passar o mouse.
 *
 * @returns o selo de bloqueio
 */
export function SeloDeBloqueio() {
  return (
    <span className="inline-flex rounded-md bg-slate-100 p-1 ring-1 ring-inset ring-slate-200">
      <IconeCadeado className="h-3.5 w-3.5 text-slate-500" titulo={TERMOS.bloqueadoParaDeploy.ajuda} />
    </span>
  )
}

/**
 * Campo que a API pode não mandar.
 *
 * "Não informado" escrito por extenso, e não uma célula em branco: célula vazia numa tabela é
 * ambígua — o usuário não sabe se ninguém preencheu ou se o sistema perdeu o dado.
 *
 * @param props o texto vindo da API
 * @returns o texto, ou o aviso de campo vazio
 */
export function ValorOuVazio({ texto }: { texto: string | null }) {
  if (!texto) {
    return <span className="text-slate-400">{TEXTOS.naoInformado}</span>
  }
  return <>{texto}</>
}

/**
 * A linha permanente que diz em qual recorte a lista está, e como chegar ao outro.
 *
 * Ela existe porque o recorte padrão passou a esconder alguma coisa: sem as 1.027 importadas na
 * primeira página, quem procura uma delas conclui que o sistema as perdeu. Um aviso que só
 * aparecesse "quando é diferente" não resolveria — a pessoa precisa saber o que está vendo
 * justamente no caso normal, que é o que ela vê todo dia.
 *
 * O link é o endereço canônico de cada recorte, o MESMO do item de menu e do cartão do painel:
 * um recorte, um endereço, e o item de menu acende junto. Ele troca o recorte inteiro, e não só a
 * procedência, porque é resposta a "onde foi parar o resto", não refinamento do filtro corrente.
 *
 * @param props procedências correntes da lista
 * @returns a linha de contexto
 */
export function LinhaDoRecorte({ origem }: { origem: OrigemGmud[] }) {
  const recorte = recorteEmVigor(origem)

  return (
    <p className="text-sm text-slate-600">
      {recorte.texto}{' '}
      <Link
        to={recorte.para}
        className="font-medium text-niteroi-700 underline underline-offset-2 hover:text-niteroi-800"
      >
        {recorte.link}
      </Link>
    </p>
  )
}

/** O que dizer e para onde apontar, em cada procedência que a lista pode estar mostrando. */
function recorteEmVigor(origem: OrigemGmud[]) {
  const { processo, historico, ambas, verHistorico, verProcesso } = TEXTOS.recorteDaLista

  if (eOrigemPadrao(origem)) {
    return { texto: processo, para: '/gmuds?origem=MIGRADO_V1', link: verHistorico }
  }
  if (origem.length === 1 && origem[0] === 'MIGRADO_V1') {
    return { texto: historico, para: '/gmuds', link: verProcesso }
  }
  // Procedência montada à mão (as duas marcadas, ou nenhuma — que para a API é o mesmo). A linha
  // não finge que a lista está num dos dois recortes nomeados: diz que está mostrando tudo.
  return { texto: ambas, para: '/gmuds', link: verProcesso }
}

/**
 * O aviso que abre a lista quando o recorte é só o histórico importado.
 *
 * A marca linha a linha diz o que cada registro é; este aviso diz o que a TELA INTEIRA é. São
 * coisas diferentes: quem chega pelo menu "Histórico do sistema anterior" precisa saber, antes
 * de ler o primeiro registro, que nada ali passou por comitê, por conferência contra o Termo de
 * Referência ou por comprovação de teste — senão exporta a página achando que exportou processo.
 *
 * É outra coisa da `LinhaDoRecorte` acima: a linha diz em que recorte a lista está e como trocar;
 * este aviso explica o que aqueles registros são.
 *
 * @returns o aviso de histórico
 */
export function AvisoDeHistorico() {
  return (
    <div className="flex gap-3 rounded-xl border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-800">
      <IconeHistorico className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
      <p className="max-w-prose">
        <strong className="font-semibold">{TERMOS.historicoMigrado.rotulo}.</strong>{' '}
        {TERMOS.historicoMigrado.ajuda}
      </p>
    </div>
  )
}
